import { Temporal } from 'temporal-polyfill'
import type { OnlineEventViewDto } from '@/api/webs'
import { splitIanaId } from './timeZones'
import { formatWallClockDay, wallClock } from './webinarRoomSetup'

/**
 * Re-run (ORB-1065): an OrbitalX-room web made again for a new date. The new session keeps this one's length, and its
 * zone unless another is chosen (ORB-1086). Everything is worked out on the new session's own wall clock, the way the
 * wire and the date and time fields carry it, and the zone is named as it goes by on the new date, which can differ
 * from this one's (BST, then GMT).
 */

/** The session a re-run repeats: its start on its own wall clock, its real length and its zone. */
export interface RerunSource {
    start: Temporal.PlainDateTime
    lengthMinutes: number
    timeZone: string
}

/** The new session, with the source's length, on the wall clock of the zone it runs in. */
export interface RerunSchedule {
    start: Temporal.PlainDateTime
    end: Temporal.PlainDateTime
    timeZone: string
}

/** The panel's fields as the inputs give them: `YYYY-MM-DD`, `HH:mm` and an IANA id, '' once the zone is cleared. */
export interface RerunForm {
    date: string
    start: string
    timeZone: string
}

export interface RerunProblem {
    field: keyof RerunForm
    message: string
}

/** The zone's abbreviation at a wall-clock time in it, as `useTimeZones().getAbbreviation` answers it. */
export type ZoneAbbreviation = (timeZone: string, wallClockTime: string) => string

const DEFAULT_DAYS_AFTER = 14

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** What a re-run never brings over: the landing page and emails are set up for the new date as usual. */
export const RERUN_NOT_COPIED = ['Landing page', 'Emails and activations', 'Broadcasts', 'Registrants', 'Chat, questions and poll answers', 'Sign-off']

/** Whether Temporal can place a wall clock in `timeZone`. */
export function isKnownTimeZone(timeZone: string): boolean {
    if (!timeZone) return false
    try {
        Temporal.Instant.fromEpochMilliseconds(0).toZonedDateTimeISO(timeZone)
        return true
    } catch {
        return false
    }
}

/**
 * The session to repeat, or null when the web has no start and end in order or no zone Temporal knows. Without
 * those there is no length to keep and nothing to name the new date's zone by.
 */
export function rerunSource(event: Pick<OnlineEventViewDto, 'StartDateTime' | 'EndDateTime' | 'TimeZone'> | null | undefined): RerunSource | null {
    const start = wallClock(event?.StartDateTime)
    const end = wallClock(event?.EndDateTime)
    const timeZone = event?.TimeZone?.trim()
    if (!start || !end || !timeZone || !isKnownTimeZone(timeZone)) return null
    // The time that really passes, as the API keeps it, which a clock change makes differ from the wall clock's.
    const lengthMinutes = start.toZonedDateTime(timeZone).until(end.toZonedDateTime(timeZone), { largestUnit: 'minutes' }).minutes
    return lengthMinutes > 0 ? { start, lengthMinutes, timeZone } : null
}

function todayIn(timeZone: string, now: Date): Temporal.PlainDate {
    return Temporal.Instant.fromEpochMilliseconds(now.getTime()).toZonedDateTimeISO(timeZone).toPlainDate()
}

/** This session's end on the wall clock of `timeZone`, where it can fall on another day than in its own. */
function sessionEndIn(source: RerunSource, timeZone: string): Temporal.PlainDateTime {
    return source.start.toZonedDateTime(source.timeZone).add({ minutes: source.lengthMinutes }).withTimeZone(timeZone).toPlainDateTime()
}

function later(first: Temporal.PlainDate, second: Temporal.PlainDate): Temporal.PlainDate {
    return Temporal.PlainDate.compare(first, second) >= 0 ? first : second
}

/**
 * The first date the new session can take in `timeZone`, a zone Temporal knows: the day after this session ends, and
 * never today or a day gone by, both as they fall in that zone. From the end, as a session can run past midnight there.
 */
export function earliestRerunDate(source: RerunSource, timeZone: string, now: Date): Temporal.PlainDate {
    return later(sessionEndIn(source, timeZone).toPlainDate().add({ days: 1 }), todayIn(timeZone, now).add({ days: 1 }))
}

/** Two weeks after this session, or two weeks from today when that has already gone by. */
export function defaultRerunDate(source: RerunSource, now: Date): Temporal.PlainDate {
    const today = todayIn(source.timeZone, now)
    const afterSession = source.start.toPlainDate().add({ days: DEFAULT_DAYS_AFTER })
    return Temporal.PlainDate.compare(afterSession, today) > 0 ? afterSession : today.add({ days: DEFAULT_DAYS_AFTER })
}

/** This session's start, as the time field takes it ("15:00"). */
export function defaultRerunStart(source: RerunSource): string {
    return source.start.toPlainTime().toString({ smallestUnit: 'minute' })
}

function parseDate(value: string): Temporal.PlainDate | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
    try {
        return Temporal.PlainDate.from(value, { overflow: 'reject' })
    } catch {
        return null
    }
}

function parseTime(value: string): Temporal.PlainTime | null {
    if (!/^\d{2}:\d{2}(?::\d{2})?$/.test(value)) return null
    try {
        return Temporal.PlainTime.from(value, { overflow: 'reject' })
    } catch {
        return null
    }
}

/**
 * What stops the re-run from being created, the first problem only, or null when nothing does. The date is checked
 * in the chosen zone, where this session and today can fall on other days than in this session's own.
 */
export function rerunProblem(source: RerunSource, form: RerunForm, now: Date): RerunProblem | null {
    const date = parseDate(form.date)
    if (!date) return { field: 'date', message: 'Pick a date.' }
    if (!isKnownTimeZone(form.timeZone)) return { field: 'timeZone', message: 'Pick a time zone.' }
    const sessionEnd = sessionEndIn(source, form.timeZone)
    if (Temporal.PlainDate.compare(date, sessionEnd.toPlainDate()) <= 0) {
        return { field: 'date', message: `Pick a date after this session on ${formatWallClockDay(sessionEnd)}.` }
    }
    if (Temporal.PlainDate.compare(date, todayIn(form.timeZone, now)) <= 0) return { field: 'date', message: 'Pick a date after today.' }
    if (!parseTime(form.start)) return { field: 'start', message: 'Pick a start time.' }
    return null
}

/**
 * The new session from the fields, with this session's length, or null until all three hold a usable value. Worked out
 * as the API stores it: a start in a clock change's gap moves on by the gap, one in a repeated hour is the second time
 * round, and the end is the length later in real time, so across a change it isn't the length on the wall clock.
 */
export function rerunSchedule(source: RerunSource, form: RerunForm): RerunSchedule | null {
    const date = parseDate(form.date)
    const time = parseTime(form.start)
    if (!date || !time || !isKnownTimeZone(form.timeZone)) return null
    const start = date.toPlainDateTime(time.round({ smallestUnit: 'minute', roundingMode: 'trunc' }))
        .toZonedDateTime(form.timeZone, { disambiguation: 'later' })
    return { start: start.toPlainDateTime(), end: start.add({ minutes: source.lengthMinutes }).toPlainDateTime(), timeZone: form.timeZone }
}

/** The new start as the API takes it: the event's wall clock with no offset, as the details patch sends it. */
export function rerunStartDateTime(schedule: RerunSchedule): string {
    return schedule.start.toString({ smallestUnit: 'minute' })
}

function clock(time: Temporal.PlainDateTime, withMeridiem: boolean): string {
    const text = `${time.hour % 12 || 12}:${String(time.minute).padStart(2, '0')}`
    return withMeridiem ? `${text} ${time.hour < 12 ? 'AM' : 'PM'}` : text
}

/** "3:00 to 3:45 PM", or "11:30 AM to 12:15 PM" when the two ends fall either side of noon. */
function clockRange(start: Temporal.PlainDateTime, end: Temporal.PlainDateTime): string {
    const sameMeridiem = (start.hour < 12) === (end.hour < 12)
    return `${clock(start, !sameMeridiem)} to ${clock(end, true)}`
}

function rerunAbbreviation(schedule: RerunSchedule, abbreviate: ZoneAbbreviation): string {
    return abbreviate(schedule.timeZone, rerunStartDateTime(schedule))
}

/** "Thursday 5 November 2026, 3:00 to 3:45 PM GMT", with the zone as it goes by on the new date. */
export function formatRerunSchedule(schedule: RerunSchedule, abbreviate: ZoneAbbreviation): string {
    const day = `${WEEKDAYS[schedule.start.dayOfWeek - 1]} ${formatWallClockDay(schedule.start)} ${schedule.start.year}`
    return `${day}, ${clockRange(schedule.start, schedule.end)} ${rerunAbbreviation(schedule, abbreviate)}`
}

/** "London time": the zone by its city, the thing that decides which abbreviation a date gets. */
export function zoneTimeName(timeZone: string): string {
    return `${splitIanaId(timeZone).city} time`
}

const unit = (count: number, singular: string) => `${count} ${singular}${count === 1 ? '' : 's'}`

/** "45 minutes", "1 hour", "1 hour 30 minutes", "2 hours". */
export function formatSessionLength(minutes: number): string {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    if (hours === 0) return unit(rest, 'minute')
    return rest === 0 ? unit(hours, 'hour') : `${unit(hours, 'hour')} ${unit(rest, 'minute')}`
}

/** The line under the fields: the new session, why its zone reads as it does on that date, and the length it keeps. */
export function rerunHelp(source: RerunSource, schedule: RerunSchedule, abbreviate: ZoneAbbreviation): string {
    const abbreviation = rerunAbbreviation(schedule, abbreviate)
    return `${formatRerunSchedule(schedule, abbreviate)}. ${zoneTimeName(schedule.timeZone)}, so ${abbreviation} on that date. `
        + `${formatSessionLength(source.lengthMinutes)}, like this one.`
}

/** What the re-run brings over. The recording and the polls are named only when this web has them. */
export function rerunCopiedItems(event: Pick<OnlineEventViewDto, 'Recording' | 'Polls'>): string[] {
    const polls = event.Polls?.length ?? 0
    return [
        'Event details, with the new date and time',
        'Speakers and agenda',
        'Audiences and lead goal',
        ...(event.Recording ? ['The recording, with no new upload'] : []),
        ...(polls > 0 ? [`${polls} ${polls === 1 ? 'poll, at the same time' : 'polls, at the same times'}`] : []),
    ]
}
